<template>
  <div class="container--fluid">
    <div class="d-flex justify-space-between">
      <h3 class="my-6">
        <v-btn icon size="small" rounded variant="text" @click="$emit('back')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        {{ title }}
      </h3>
      <span
        class="cursor-pointer mt-6"
        :title="t('customForms.tooltips.clickToEnableDisableForm')"
        data-testid="toggle-trigger"
        @click="isToggleTriggerConfirmVisible = true"
        v-if="trigger?.flow && !isLoadTypeTrigger">
        {{ t('global.basicTerms.forms') }} {{ statusText(trigger, true) }}
        <v-icon :color="trigger.isActive ? 'primary' : 'grey'" size="xx-large" class="ml-1">
          {{ trigger.isActive ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}
        </v-icon>
      </span>
    </div>
    <v-container fluid v-if="trigger?.flow?.formFrom">
      <div
        class="d-flex flex-row justify-space-between align-center pa-2 border-radius border rounded mb-6"
        v-if="isLoadTypeTrigger">
        <div class="typography-body-md pa-2" data-testid="loadtype-form-header">
          <v-icon size="small" class="mr-1">mdi-text-box-edit-outline</v-icon>
          <strong>{{ trigger.flow.formFrom.name }}</strong>

          <p class="typography-body-sm mt-2" v-if="loadTypeNames">
            <strong>{{ t('customForms.messages.requestedFor') }}:</strong>
            {{ loadTypeNames }}
          </p>
        </div>
        <v-menu location="bottom left">
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              variant="flat"
              size="x-large"
              v-bind="props"
              data-testid="loadtype-form-menu">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item
              density="compact"
              @click="isEditFormDialogVisible = true"
              data-testid="loadtype-form-menu-edit">
              <v-icon size="x-small" class="mr-2">mdi-pencil</v-icon>
              {{ t('global.buttons.edit') }}
            </v-list-item>
            <v-list-item
              @click="isDeleteConfirmationVisible = true"
              density="compact"
              class="text-error"
              data-testid="loadtype-form-menu-delete">
              <v-icon size="x-small" class="mr-2" color="error">mdi-delete-outline</v-icon>
              {{ t('customForms.buttons.deleteForm') }}
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <template v-if="trigger.flow?.parentCode && relatedTriggers?.length > 1">
        <v-tabs v-model="activeTab" class="mb-4" color="primary">
          <v-tab
            v-for="appName of triggerApps"
            :value="appName"
            :key="appName"
            :data-testid="`loadtype-form-${appName}-tab`">
            <span class="mx-10">{{ getAppTabName(appName) }}</span>
          </v-tab>
        </v-tabs>
      </template>

      <v-window v-model="activeTab">
        <v-window-item :value="appName" v-for="appName of triggerApps" :key="appName">
          <div class="d-flex flex-row justify-space-between align-center my-4">
            <p>
              <span v-html="getAppHelperText(appName)" class="mr-1"></span>
              <a href="#" @click.prevent="isPreviewDialogVisible[appName] = true">
                {{ t('customForms.messages.seeWhere') }}.
              </a>
            </p>

            <div
              v-if="isLoadTypeTrigger && relatedTriggers?.length > 1"
              class="cursor-pointer"
              :data-testid="`toggle-trigger-${appName}`"
              :title="t('customForms.tooltips.clickToEnableDisableForm')"
              @click="isToggleTriggersConfirmVisible[appName] = true">
              <p>
                {{ t('global.basicTerms.forms') }} {{ statusText(getTrigger(appName), true) }}
                <v-icon
                  :color="getTrigger(appName).isActive ? 'primary' : 'grey'"
                  size="xx-large"
                  class="ml-1">
                  {{
                    getTrigger(appName).isActive
                      ? 'mdi-toggle-switch'
                      : 'mdi-toggle-switch-off-outline'
                  }}
                </v-icon>
              </p>
            </div>
          </div>

          <custom-forms-flow
            :readonly="$isWarehouseReadOnly(warehouse.id, warehouse.orgId)"
            :warehouse="warehouse"
            :app="appName"
            @data-updated="$emit('data-updated')"
            :trigger="getTrigger(appName)" />

          <custom-forms-preview-dialog
            :app="activeTab"
            @close="isPreviewDialogVisible[appName] = false"
            :is-visible="isPreviewDialogVisible[appName]" />

          <custom-forms-edit-load-type-trigger-dialog
            v-if="isLoadTypeTrigger"
            :warehouse="warehouse"
            :triggers="relatedTriggers"
            @form-updated="
              $emit('data-updated');
              $emit('back');
            "
            @close="isEditFormDialogVisible = false"
            :is-visible="isEditFormDialogVisible" />

          <confirm
            :should-show="isToggleTriggersConfirmVisible[appName]"
            is-manual-mode
            persistent
            :title="`${capitalize(statusText(getTrigger(appName), false, true))} ${t(
              'customForms.messages.thisForm'
            )}`"
            :width="650"
            @result="value => handleFormActiveChange(value, getTrigger(appName))">
            <template v-slot:message>
              <p>
                <i18n-t keypath="customForms.messages.areYouSureYouWantToActionTheTitle">
                  <template #action>
                    <strong>{{ statusText(getTrigger(appName), false, true) }}</strong>
                  </template>
                  <template #title>{{ title }}</template>
                </i18n-t>
              </p>

              <template v-if="featureData.enableDisclaimer && featureData.disableDisclaimer">
                <p
                  class="my-4"
                  v-html="
                    getTrigger(appName).isActive
                      ? featureData.disableDisclaimer
                      : featureData.enableDisclaimer
                  "></p>
              </template>
            </template>
          </confirm>
        </v-window-item>
      </v-window>
    </v-container>

    <p v-else>{{ t('customForms.messages.formNotAvailable') }}</p>

    <confirm
      v-if="isLoadTypeTrigger"
      :should-show="isDeleteConfirmationVisible"
      delete-confirmation
      show-delete-warning
      persistent
      @result="handleDeleteConfirmationResult"
      title="Delete this Form?"
      :entity-name="trigger.flow?.formFrom?.name"
      button-true-text="YES, DELETE"
      button-true-color="error"
      :width="550">
      <template v-slot:message>
        <p>
          <strong class="text-error">Important:</strong>
          Submitted data will be preserved, but the form will
          <strong>removed from appointment scheduling,</strong>
          and existing submissions
          <strong>cannot be updated.</strong>
        </p>
      </template>
    </confirm>

    <confirm
      :should-show="isToggleTriggerConfirmVisible"
      is-manual-mode
      persistent
      :title="`${capitalize(statusText(trigger, false, true))} this form`"
      :width="650"
      @result="value => handleFormActiveChange(value, trigger)">
      <template v-slot:message>
        <p>
          <i18n-t keypath="customForms.messages.areYouSureYouWantToActionTheTitle">
            <template #action>
              <strong>{{ statusText(trigger, false, true) }}</strong>
            </template>
            <template #title>{{ title }}</template>
          </i18n-t>
        </p>

        <template v-if="featureData.enableDisclaimer && featureData.disableDisclaimer">
          <p
            class="my-4"
            v-html="
              trigger.isActive ? featureData.disableDisclaimer : featureData.enableDisclaimer
            "></p>
        </template>
      </template>
    </confirm>
  </div>
</template>

<script>
import { camelCase, capitalize } from 'lodash';
import { defineComponent, computed, ref } from 'vue';
import {
  CustomFormsFeaturesData,
  CustomFormFlowEntitiesEnum,
  AppsEnum,
  getLoadTypesNames,
  getTranslatedCustomFormsFeaturesData
} from '@nova/core';
import { useMixpanel, useNotify } from '@/composables';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'CustomFormsTrigger',
  props: {
    warehouse: {
      type: Object,
      required: true
    },
    trigger: {
      type: Object,
      required: true
    },
    /* triggers that have the same flow.parentCode */
    relatedTriggers: {
      type: Array,
      required: false
    }
  },
  emits: ['back', 'data-updated'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const notify = useNotify();
    const mixpanel = useMixpanel();
    const isDeleteConfirmationVisible = ref(false);
    const isEditFormDialogVisible = ref(false);
    const isToggleTriggerConfirmVisible = ref(false);
    const isToggleTriggersConfirmVisible = ref({
      [AppsEnum.All]: false,
      [AppsEnum.Carrier]: false,
      [AppsEnum.Warehouse]: false,
      [AppsEnum.Driver]: false,
      [AppsEnum.Api]: false
    });
    const isPreviewDialogVisible = ref({
      [AppsEnum.All]: false,
      [AppsEnum.Carrier]: false,
      [AppsEnum.Warehouse]: false,
      [AppsEnum.Driver]: false,
      [AppsEnum.Api]: false
    });

    const activeTab = ref(props.trigger.app);

    const title = computed(() => {
      let text = getTranslatedCustomFormsFeaturesData()[props.trigger.feature].title;

      if (props.relatedTriggers.length <= 1) {
        text = `${text} ${t('customForms.messages.formFor')} ${capitalize(props.trigger.app)}`;
      } else {
        text = `${text} ${t('global.basicTerms.forms', 2)}`;
      }

      return text;
    });

    const featureData = computed(() => CustomFormsFeaturesData[props.trigger.feature]);

    const isLoadTypeTrigger = computed(
      () => props.trigger.entityName === CustomFormFlowEntitiesEnum.loadtype
    );

    const trackMixpanelFormStatusChange = status => {
      const eventName = status ? 'ENABLE_FORM' : 'DISABLE_FORM';
      return mixpanel?.track(mixpanel.events.MODULE.CUSTOM_FORMS[eventName], {
        'Custom Form Application': props.trigger.app,
        'Custom Form Type': props.trigger.feature,
        'Warehouse ID': props.warehouse.id,
        'Warehouse Name': props.warehouse.name,
        'Org ID': props.warehouse.orgId,
        'Org Name': props.warehouse.org?.name,
        'Flow ID': props.trigger.flow?.id,
        'Flow Name': props.trigger.flow?.name
      });
    };

    const statusText = (trigger, past = true, inverse = false) => {
      const condition = inverse ? !trigger.isActive : trigger.isActive;
      const text = condition ? t('global.basicTerms.enable') : t('global.basicTerms.disable');
      return past ? `${text}d` : text;
    };

    const handleFormActiveChange = async (value, trigger) => {
      isToggleTriggerConfirmVisible.value = false;
      isToggleTriggersConfirmVisible.value[trigger.app] = false;

      if (value) {
        const response = await axios.patch(`custom-forms/trigger/${trigger.id}`, {
          isActive: !trigger.isActive,
          updateRelatedTriggers: true
        });

        if (response?.data?.data) {
          notify(
            `${title.value} ${t('global.basicTerms.is')} ${
              trigger.isActive ? t('global.basicTerms.enabled') : t('global.basicTerms.disabled')
            }`,
            'success'
          );
          emit('data-updated');
          trackMixpanelFormStatusChange(trigger.isActive);
        }
      }
    };

    const handleDeleteConfirmationResult = async value => {
      isDeleteConfirmationVisible.value = false;

      if (value) {
        try {
          await axios.delete(`custom-forms/flow/${props.trigger.flow.id}`);
          notify(t('customForms.messages.formDeletedSuccessfully'), 'success');

          emit('back');
          emit('data-updated');
        } catch (error) {
          console.error('Error deleting form:', error);
          notify(t('customForms.messages.errorDeletingForm'), 'error');
        }
      }
    };

    const triggerApps = computed(() => {
      if (!props.relatedTriggers) {
        return [props.trigger.app];
      }

      const apps = [
        ...new Set([...(props.relatedTriggers?.map(t => t.app) || []), props.trigger.app])
      ];

      if (apps.includes(AppsEnum.All)) {
        return [AppsEnum.All, ...apps.filter(app => app !== AppsEnum.All).sort()];
      }

      return apps.sort();
    });

    const getTrigger = appName => {
      if (!props.relatedTriggers || props.relatedTriggers.length === 0) {
        return props.trigger;
      }

      return props.relatedTriggers.find(t => t.app === appName) || props.trigger;
    };

    const getAppTabName = appName => {
      if (appName === AppsEnum.All) {
        return t('customForms.messages.visibleToAll');
      }
      return `${t(`customForms.messages.${camelCase(appName)}Only`).toUpperCase()}`;
    };

    const getAppHelperText = appName => {
      if (appName === AppsEnum.All) {
        return t('customForms.messages.fieldsAddedHereWillBeDisplayedForBothWarehouseAndCarriers');
      }

      if (appName === AppsEnum.Carrier) {
        return t('customForms.messages.fieldsAddedHereWillBeDisplayedForCarriersOnly');
      }

      if (appName === AppsEnum.Warehouse) {
        return t('customForms.messages.fieldsAddedHereWillBeDisplayedForWarehousesOnly');
      }

      if (appName === AppsEnum.Driver) {
        return t('customForms.messages.fieldsAddedHereWillBeDisplayedForDriversCheckInAppOnly');
      }

      if (appName === AppsEnum.Api) {
        return t('customForms.messages.fieldsAddedHereWillBeRequestForApiIntegrationsOnly');
      }
    };

    const loadTypeNames = computed(() =>
      getLoadTypesNames(props.relatedTriggers.map(t => t.loadType))
    );

    return {
      isToggleTriggerConfirmVisible,
      isToggleTriggersConfirmVisible,
      AppsEnum,
      activeTab,
      isLoadTypeTrigger,
      isDeleteConfirmationVisible,
      isEditFormDialogVisible,
      handleDeleteConfirmationResult,
      loadTypeNames,
      getTrigger,
      title,
      getAppTabName,
      featureData,
      triggerApps,
      capitalize,
      getAppHelperText,
      isPreviewDialogVisible,
      CustomFormsFeaturesData,
      CustomFormFlowEntitiesEnum,
      statusText,
      handleFormActiveChange,
      t
    };
  }
});
</script>
